import { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    Select,
    MenuItem,
    FormControl,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Autocomplete,
    TextField,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider
} from "@mui/material";
import {
    CheckCircle as SuccessIcon,
    Error as ErrorIcon,
    SkipNext as SkipIcon
} from "@mui/icons-material";
import { FileUploader } from "react-drag-drop-files";
import { CloudUpload as UploadIcon } from "@mui/icons-material";
import PageHeader from "../componenets/shared/PageHeader";
import { useAuth } from "../contexts/AuthContext";
import { useCompany } from "../contexts/CompanyContext";
import { useMessageContext } from "../contexts/MessageContext";
import {
    parseFile,
    transformToUsers,
    FIELD_OPTIONS,
    FIELD_DEFINITIONS
} from "../utils/importUsersParser";
// This page is a SUPER-ADMIN tool (SideNav shows it only for a super-admin): the caller picks the
// target company, so both calls go to /system — the /admin twins take the company from the
// principal and refuse a company-less super-admin with a 403.
import { getCompanyTrainers } from "../services/system/TrainerService";
import { importTrainees } from "../services/system/TraineeService";
import {RTL} from "../cache/CacheRtl";

const FILE_TYPES = ["CSV", "XLSX", "XLS"];

const ImportUsersPage = () => {
    const { isSuperAdmin } = useAuth();
    const { getCompaniesList } = useCompany();
    const { notificationHandler } = useMessageContext();

    const [loading, setLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [fileData, setFileData] = useState(null);
    const [columnMappings, setColumnMappings] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [selectedTrainer, setSelectedTrainer] = useState(null);
    const [trainers, setTrainers] = useState([]);
    const [loadingTrainers, setLoadingTrainers] = useState(false);
    const [password, setPassword] = useState("");
    const [fileName, setFileName] = useState("");
    const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
    const [importResult, setImportResult] = useState(null);

    // Load trainers when company changes
    useEffect(() => {
        const loadTrainers = async () => {
            if (!selectedCompany) {
                setTrainers([]);
                setSelectedTrainer(null);
                return;
            }

            setLoadingTrainers(true);
            try {
                const response = await getCompanyTrainers(selectedCompany.id);
                setTrainers(response.data || []);
                setSelectedTrainer(null);
            } catch (error) {
                console.error("Error loading trainers:", error);
                notificationHandler.error("שגיאה בטעינת מאמנים");
                setTrainers([]);
            } finally {
                setLoadingTrainers(false);
            }
        };

        loadTrainers();
    }, [selectedCompany]);

    const handleFileUpload = async (file) => {
        setLoading(true);
        setFileName(file.name);

        try {
            const result = await parseFile(file);
            setFileData(result);
            setColumnMappings(result.columnMappings);
        } catch (error) {
            notificationHandler.error(error.message || "שגיאה בקריאת הקובץ");
            setFileData(null);
            setColumnMappings([]);
        } finally {
            setLoading(false);
        }
    };

    const handleColumnMappingChange = (columnIndex, newField) => {
        setColumnMappings(prev =>
            prev.map(mapping =>
                mapping.index === columnIndex
                    ? { ...mapping, detectedField: newField }
                    : mapping
            )
        );
    };

    const handleClearFile = () => {
        setFileData(null);
        setColumnMappings([]);
        setFileName("");
    };

    const handleImport = async () => {
        const users = transformToUsers(fileData.rawRows, columnMappings);

        // Build the request
        const request = {
            companyId: selectedCompany?.id,
            trainerId: selectedTrainer?.id,
            password: password,
            trainees: users.map(user => ({
                name: user.name,
                email: user.email,
                phone: user.phone,
                dateOfBirth: user.dateOfBirth,
                gender: user.gender,
                weight: user.weight ? parseFloat(user.weight) : null,
                height: user.height ? parseFloat(user.height) : null
            }))
        };

        setImporting(true);
        try {
            const response = await importTrainees(request);
            const result = response.data;

            // Store the result and show summary dialog
            setImportResult(result);
            setSummaryDialogOpen(true);

        } catch (error) {
            console.error("Import error:", error);
            notificationHandler.error(error.response?.data?.message || "שגיאה בייבוא המשתמשים");
        } finally {
            setImporting(false);
        }
    };

    const handleCloseSummaryDialog = () => {
        setSummaryDialogOpen(false);

        // Clear form if there were successful imports
        if (importResult && (importResult.totalCreated > 0 || importResult.totalUpdated > 0)) {
            handleClearFile();
            setSelectedCompany(null);
            setSelectedTrainer(null);
            setPassword("");
        }

        setImportResult(null);
    };

    // Check if there are any unresolved (unknown) columns
    const hasUnresolvedColumns = columnMappings.some(m => m.detectedField === 'unknown');

    return (
        <Box>
            <PageHeader title="ייבוא משתמשים" />

            <Box sx={{
                paddingRight: { md: "28px", xs: "14px" },
                paddingLeft: { md: "28px", xs: "14px" },
                paddingTop: "20px"
            }}>
                <Typography sx={{
                    fontSize: "16px",
                    fontWeight: 400,
                    color: "#6B7280",
                    marginBottom: "24px",
                    textAlign: "right"
                }}>
                    העלו קובץ CSV או Excel עם נתוני המשתמשים. המערכת תזהה אוטומטית את העמודות ותאפשר לכם לתקן את המיפוי.
                </Typography>

                {/* File Upload Area */}
                {!fileData && (
                    <Box sx={{ marginBottom: "24px" }}>
                        <FileUploader
                            handleChange={handleFileUpload}
                            name="file"
                            types={FILE_TYPES}
                            disabled={loading}
                        >
                            <Box sx={{
                                border: "2px dashed #D1D5DB",
                                borderRadius: "12px",
                                padding: "48px 24px",
                                textAlign: "center",
                                cursor: loading ? "not-allowed" : "pointer",
                                backgroundColor: "#FAFBFC",
                                transition: "all 0.2s ease",
                                "&:hover": {
                                    borderColor: "#57837E",
                                    backgroundColor: "#F0F4F3"
                                }
                            }}>
                                {loading ? (
                                    <CircularProgress size={48} sx={{ color: "#57837E" }} />
                                ) : (
                                    <>
                                        <UploadIcon sx={{ fontSize: 48, color: "#9CA3AF", marginBottom: "16px" }} />
                                        <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "#374151", marginBottom: "8px" }}>
                                            גררו קובץ לכאן או לחצו לבחירה
                                        </Typography>
                                        <Typography sx={{ fontSize: "14px", color: "#9CA3AF" }}>
                                            CSV, XLSX, XLS
                                        </Typography>
                                    </>
                                )}
                            </Box>
                        </FileUploader>
                    </Box>
                )}

                {/* File Info & Clear Button */}
                {fileData && (
                    <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "24px",
                        padding: "16px",
                        backgroundColor: "#F0F4F3",
                        borderRadius: "8px"
                    }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#374151" }}>
                                {fileName}
                            </Typography>
                            <Chip
                                label={`${fileData.totalRows} שורות`}
                                size="small"
                                sx={{ backgroundColor: "#57837E", color: "white" }}
                            />
                        </Box>
                        <Button
                            onClick={handleClearFile}
                            sx={{
                                color: "#EF4444",
                                fontSize: "14px",
                                textTransform: "none"
                            }}
                        >
                            נקה קובץ
                        </Button>
                    </Box>
                )}

                {/* Column Mapping */}
                {fileData && columnMappings.length > 0 && (
                    <Box sx={{ marginBottom: "24px" }}>
                        <Typography sx={{
                            fontSize: "16px",
                            fontWeight: 600,
                            color: "#1F2937",
                            marginBottom: "16px",
                            textAlign: "right"
                        }}>
                            מיפוי עמודות
                        </Typography>
                        <Paper sx={{
                            padding: "16px",
                            backgroundColor: "#FFFFFF",
                            border: "1px solid #E2E8F0",
                            borderRadius: "8px",
                            overflowX: "auto"
                        }}>
                            <Box sx={{
                                display: "flex",
                                gap: "12px",
                                flexWrap: "nowrap",
                                minWidth: "fit-content"
                            }}>
                                {columnMappings.map((mapping) => (
                                    <Box key={mapping.index} sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "8px",
                                        minWidth: "140px"
                                    }}>
                                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
                                            <Typography sx={{
                                                fontSize: "11px",
                                                color: "#57837E",
                                                fontWeight: 600
                                            }}>
                                                עמודה {mapping.index + 1}
                                            </Typography>
                                            <Typography sx={{
                                                fontSize: "12px",
                                                color: mapping.detectedField === 'unknown' ? "#F59E0B"
                                                    : mapping.detectedField === 'ignore' ? "#9CA3AF"
                                                    : "#57837E",
                                                textAlign: "center",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                                maxWidth: "130px",
                                                fontWeight: mapping.detectedField !== 'unknown' && mapping.detectedField !== 'ignore' ? 600 : 400
                                            }}>
                                                {mapping.detectedField === 'unknown' || mapping.detectedField === 'ignore'
                                                    ? mapping.originalHeader
                                                    : FIELD_DEFINITIONS[mapping.detectedField]?.label || mapping.originalHeader}
                                            </Typography>
                                        </Box>
                                        <RTL>
                                        <FormControl size="small" fullWidth>
                                            <Select
                                                value={mapping.detectedField}
                                                onChange={(e) => handleColumnMappingChange(mapping.index, e.target.value)}
                                                sx={{
                                                    fontSize: "14px",
                                                    "& .MuiOutlinedInput-notchedOutline": {
                                                        borderColor: mapping.detectedField === 'unknown' ? "#F59E0B"
                                                            : mapping.detectedField === 'ignore' ? "#D1D5DB"
                                                            : "#57837E"
                                                    },
                                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                                        borderColor: "#57837E"
                                                    },
                                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                        borderColor: "#57837E"
                                                    }
                                                }}
                                            >
                                                {FIELD_OPTIONS.map(option => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        </RTL>
                                    </Box>
                                ))}
                            </Box>
                        </Paper>
                    </Box>
                )}

                {/* Preview Table - Shows ALL columns from the file */}
                {fileData && fileData.rawRows.length > 0 && (
                    <Box sx={{
                        backgroundColor: "#FFFFFF",
                        border: "1px solid #E2E8F0",
                        borderRadius: "8px",
                        overflow: "hidden",
                        marginBottom: "24px"
                    }}>
                        <Box sx={{
                            padding: "20px 24px",
                            borderBottom: "1px solid #E2E8F0",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center"
                        }}>
                            <Typography sx={{
                                fontSize: "18px",
                                fontWeight: 600,
                                color: "#1F2937",
                                textAlign: "right"
                            }}>
                                תצוגה מקדימה
                            </Typography>
                            <Typography sx={{ fontSize: "14px", color: "#6B7280" }}>
                                מציג {Math.min(fileData.rawRows.length, 50)} מתוך {fileData.rawRows.length} שורות | {columnMappings.length} עמודות
                            </Typography>
                        </Box>

                        <TableContainer sx={{ maxHeight: "400px", overflowX: "auto" }}>
                            <Table stickyHeader sx={{ minWidth: columnMappings.length * 150 }}>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: "#F8FAFC" }}>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                borderBottom: "1px solid #E2E8F0",
                                                padding: "12px 16px",
                                                color: "#64748B",
                                                fontSize: "12px",
                                                fontWeight: 600,
                                                backgroundColor: "#F8FAFC",
                                                width: "60px",
                                                position: "sticky",
                                                left: 0,
                                                zIndex: 3
                                            }}
                                        >
                                            #
                                        </TableCell>
                                        {columnMappings.map((mapping, idx) => (
                                            <TableCell
                                                key={idx}
                                                align="right"
                                                sx={{
                                                    borderBottom: "1px solid #E2E8F0",
                                                    padding: "8px 16px",
                                                    color: mapping.detectedField === 'unknown' ? "#F59E0B"
                                                        : mapping.detectedField === 'ignore' ? "#9CA3AF"
                                                        : "#57837E",
                                                    fontSize: "12px",
                                                    fontWeight: 600,
                                                    backgroundColor: mapping.detectedField === 'unknown' ? "#FEF3C7"
                                                        : mapping.detectedField === 'ignore' ? "#F8FAFC"
                                                        : "#E8F5F3",
                                                    minWidth: "120px"
                                                }}
                                            >
                                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "2px" }}>
                                                    <Typography sx={{ fontSize: "10px", color: "#9CA3AF", fontWeight: 500 }}>
                                                        עמודה {mapping.index + 1}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>
                                                        {mapping.detectedField === 'ignore' || mapping.detectedField === 'unknown'
                                                            ? (mapping.originalHeader || 'לא ידוע')
                                                            : FIELD_DEFINITIONS[mapping.detectedField]?.label || mapping.originalHeader}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {fileData.rawRows.slice(0, 50).map((row, rowIndex) => (
                                        <TableRow key={rowIndex} sx={{
                                            "&:hover": { backgroundColor: "#F8FAFC" },
                                            borderBottom: "1px solid #E2E8F0"
                                        }}>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    padding: "12px 16px",
                                                    fontSize: "14px",
                                                    color: "#9CA3AF",
                                                    backgroundColor: "#FFFFFF",
                                                    position: "sticky",
                                                    left: 0,
                                                    zIndex: 1
                                                }}
                                            >
                                                {rowIndex + 1}
                                            </TableCell>
                                            {columnMappings.map((mapping, colIdx) => (
                                                <TableCell
                                                    key={colIdx}
                                                    align="right"
                                                    sx={{
                                                        padding: "12px 16px",
                                                        fontSize: "14px",
                                                        color: row[mapping.index] ? "#374151" : "#D1D5DB",
                                                        backgroundColor: mapping.detectedField === 'unknown' ? "#FFFBEB"
                                                            : mapping.detectedField === 'ignore' ? "#FAFAFA"
                                                            : "inherit"
                                                    }}
                                                >
                                                    {row[mapping.index] || "—"}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}

                {/* Action Bar */}
                {fileData && (
                    <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "20px 24px",
                        backgroundColor: "#FFFFFF",
                        border: "1px solid #E2E8F0",
                        borderRadius: "8px",
                        flexWrap: "wrap",
                        gap: "16px"
                    }}>
                        {/* Selectors */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
                            {isSuperAdmin() && (
                                <>
                                    {/* Company Selector */}
                                    <RTL>
                                    <Autocomplete
                                        options={getCompaniesList()}
                                        getOptionLabel={(option) => option.name || ''}
                                        value={selectedCompany}
                                        onChange={(event, newValue) => setSelectedCompany(newValue)}
                                        sx={{ minWidth: "200px" }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="בחר חברה"
                                                size="small"
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        "&:hover fieldset": { borderColor: "#57837E" },
                                                        "&.Mui-focused fieldset": { borderColor: "#57837E" }
                                                    },
                                                    "& .MuiInputLabel-root.Mui-focused": { color: "#57837E" }
                                                }}
                                            />
                                        )}
                                    />
                                    </RTL>

                                    {/* Trainer Selector */}
                                    <RTL>
                                    <Autocomplete
                                        options={trainers}
                                        getOptionLabel={(option) => option.user?.name || option.name || ''}
                                        value={selectedTrainer}
                                        onChange={(event, newValue) => setSelectedTrainer(newValue)}
                                        disabled={!selectedCompany || loadingTrainers}
                                        loading={loadingTrainers}
                                        sx={{ minWidth: "200px" }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="בחר מאמן"
                                                size="small"
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        "&:hover fieldset": { borderColor: "#57837E" },
                                                        "&.Mui-focused fieldset": { borderColor: "#57837E" }
                                                    },
                                                    "& .MuiInputLabel-root.Mui-focused": { color: "#57837E" }
                                                }}
                                            />
                                        )}
                                    />
                                    </RTL>

                                    {/* Password Field */}
                                    <RTL>
                                    <TextField
                                        label="סיסמה למשתמשים"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        size="small"
                                        type="text"
                                        sx={{
                                            minWidth: "150px",
                                            "& .MuiOutlinedInput-root": {
                                                "&:hover fieldset": { borderColor: "#57837E" },
                                                "&.Mui-focused fieldset": { borderColor: "#57837E" }
                                            },
                                            "& .MuiInputLabel-root.Mui-focused": { color: "#57837E" }
                                        }}
                                    />
                                    </RTL>
                                </>
                            )}
                        </Box>

                        {/* Import Button */}
                        <Button
                            variant="contained"
                            onClick={handleImport}
                            disabled={
                                importing ||
                                hasUnresolvedColumns ||
                                (isSuperAdmin() && (!selectedCompany || !selectedTrainer || !password))
                            }
                            sx={{
                                backgroundColor: "#57837E",
                                color: "white",
                                fontWeight: 600,
                                height: "44px",
                                minWidth: "140px",
                                fontSize: "14px",
                                padding: "10px 24px",
                                textTransform: "none",
                                "&:hover": { backgroundColor: "#466963" },
                                "&:disabled": {
                                    backgroundColor: "#D1D5DB",
                                    color: "#9CA3AF"
                                }
                            }}
                        >
                            {importing ? <CircularProgress size={24} sx={{ color: "white" }} /> : "ייבא משתמשים"}
                        </Button>
                    </Box>
                )}
            </Box>

            {/* Import Summary Dialog */}
            <Dialog
                open={summaryDialogOpen}
                onClose={handleCloseSummaryDialog}
                maxWidth="sm"
                fullWidth
                dir="rtl"
            >
                <DialogTitle sx={{
                    backgroundColor: "#57837E",
                    color: "white",
                    fontWeight: 600
                }}>
                    סיכום ייבוא
                </DialogTitle>
                <DialogContent sx={{ padding: "24px" }}>
                    {importResult && (
                        <>
                            {/* Summary Stats */}
                            <Box sx={{
                                display: "flex",
                                gap: "16px",
                                marginBottom: "24px",
                                marginTop: "16px",
                                flexWrap: "wrap"
                            }}>
                                <Box sx={{
                                    flex: 1,
                                    minWidth: "120px",
                                    padding: "16px",
                                    backgroundColor: "#E8F5E9",
                                    borderRadius: "8px",
                                    textAlign: "center"
                                }}>
                                    <Typography sx={{ fontSize: "28px", fontWeight: 700, color: "#2E7D32" }}>
                                        {importResult.totalCreated}
                                    </Typography>
                                    <Typography sx={{ fontSize: "14px", color: "#2E7D32" }}>
                                        נוצרו בהצלחה
                                    </Typography>
                                </Box>
                                <Box sx={{
                                    flex: 1,
                                    minWidth: "120px",
                                    padding: "16px",
                                    backgroundColor: "#E3F2FD",
                                    borderRadius: "8px",
                                    textAlign: "center"
                                }}>
                                    <Typography sx={{ fontSize: "28px", fontWeight: 700, color: "#1565C0" }}>
                                        {importResult.totalUpdated}
                                    </Typography>
                                    <Typography sx={{ fontSize: "14px", color: "#1565C0" }}>
                                        עודכנו
                                    </Typography>
                                </Box>
                                <Box sx={{
                                    flex: 1,
                                    minWidth: "120px",
                                    padding: "16px",
                                    backgroundColor: "#FFF3E0",
                                    borderRadius: "8px",
                                    textAlign: "center"
                                }}>
                                    <Typography sx={{ fontSize: "28px", fontWeight: 700, color: "#E65100" }}>
                                        {importResult.totalSkipped}
                                    </Typography>
                                    <Typography sx={{ fontSize: "14px", color: "#E65100" }}>
                                        דולגו
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Errors List */}
                            {importResult.errors && importResult.errors.length > 0 && (
                                <>
                                    <Divider sx={{ marginBottom: "16px" }} />
                                    <Typography sx={{
                                        fontSize: "16px",
                                        fontWeight: 600,
                                        color: "#374151",
                                        marginBottom: "12px"
                                    }}>
                                        פרטי שגיאות ({importResult.errors.length})
                                    </Typography>
                                    <List sx={{
                                        maxHeight: "250px",
                                        overflow: "auto",
                                        backgroundColor: "#FAFAFA",
                                        borderRadius: "8px",
                                        padding: "8px"
                                    }}>
                                        {importResult.errors.map((error, index) => (
                                            <ListItem key={index} sx={{
                                                padding: "8px 12px",
                                                borderBottom: index < importResult.errors.length - 1 ? "1px solid #E5E7EB" : "none"
                                            }}>
                                                <ListItemIcon sx={{ minWidth: "36px" }}>
                                                    {error.reason.includes("already exists") ? (
                                                        <SkipIcon sx={{ color: "#F59E0B" }} />
                                                    ) : (
                                                        <ErrorIcon sx={{ color: "#EF4444" }} />
                                                    )}
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={
                                                        <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                                                            שורה {error.rowIndex}: {error.name || "ללא שם"}
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>
                                                            {error.email && `${error.email} - `}{error.reason}
                                                        </Typography>
                                                    }
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </>
                            )}

                            {/* Success message if no errors */}
                            {(!importResult.errors || importResult.errors.length === 0) && importResult.totalCreated > 0 && (
                                <Box sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "12px",
                                    padding: "16px",
                                    backgroundColor: "#E8F5E9",
                                    borderRadius: "8px"
                                }}>
                                    <SuccessIcon sx={{ color: "#2E7D32", fontSize: 32 }} />
                                    <Typography sx={{ fontSize: "16px", color: "#2E7D32" }}>
                                        כל המשתמשים יובאו בהצלחה!
                                    </Typography>
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ padding: "16px 24px" }}>
                    <Button
                        onClick={handleCloseSummaryDialog}
                        variant="contained"
                        sx={{
                            backgroundColor: "#57837E",
                            "&:hover": { backgroundColor: "#466963" }
                        }}
                    >
                        סגור
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ImportUsersPage;
